import { getBrowserOS } from 'flow/service/util';
import type { KeyboardShortcut } from './types';
import { editableHtmlElements, nonTextInputTypes } from './constants';

/**
 * determines if the modifier key is pressed across operating systems
 */
function isPrimaryModifierPressed(event: KeyboardEvent) {
  const browserOS = getBrowserOS();
  const eventMetaKey: keyof Pick<KeyboardEvent, 'metaKey' | 'ctrlKey'> = browserOS === 'mac'
    ? 'metaKey'
    : 'ctrlKey';

  return event[eventMetaKey];
}

/**
 * Formats a keyboard shortcut using the primary modifier for the current platform.
 */
export function formatKeyboardShortcut(shortcut: KeyboardShortcut): string {
  const labels: string[] = [];

  if (shortcut.primaryModifier) {
    labels.push(getBrowserOS() === 'mac' ? 'Cmd' : 'Ctrl');
  }

  if (shortcut.shiftKey) {
    labels.push('Shift');
  }

  labels.push(shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key);

  return labels.join(' + ');
}

/**
 * Determines whether a keyboard event originated from an element the user is
 * typing into (text input, textarea, select, or a contenteditable element).
 */
export function isTargetTextEditable(event: KeyboardEvent): boolean {
  const { target } = event;

  if (!(target instanceof HTMLElement)) return false;

  const { tagName } = target;

  if (target instanceof HTMLInputElement && nonTextInputTypes.has(target.type)) {
    return false;
  }

  if (editableHtmlElements.has(tagName.toUpperCase())) {
    return true;
  }

  return Boolean(target.isContentEditable);
}

export function isValidKeyboardEvent(
  event: KeyboardEvent,
  keyboardShortcut: KeyboardShortcut,
) {
  const {
    key,
    shiftKey,
    primaryModifier,
  } = keyboardShortcut;

  if (event.key !== key) return false;
  if (event.shiftKey !== Boolean(shiftKey)) return false;
  if (isPrimaryModifierPressed(event) !== Boolean(primaryModifier)) return false;

  return true;
}
